import { useState } from 'react';
import { logEvent } from "../../utils/analytics";
import { formatDateToString } from '../../utils/date';

import Alert from '../shared/Alert';
import Button from '../forms/Button';
import Loading from '../shared/Loading';
import CandidatesGroup from './CandidatesGroup';
import RaceFilters from './RaceFilters';
import { BiFilter, BiInfoCircle } from 'react-icons/bi';

const groups = [
    {
        name: 'LGA',
        role: 'LG Chairman',
        race: 'local_government',
        article: 'an',
        articleRoute: '/article/lga-chairman',
        resourceLink: null,
        runningMate: 'Vice Chairman',
        color: 'teal'
    },
    {
        name: 'LGA',
        role: 'LCDA Chairman',
        article: 'an',
        race: 'lcda',
        articleRoute: '/article/lga-chairman',
        resourceLink: null,
        runningMate: null,
        color: 'teal'
    },
    {
        name: 'Ward',
        role: 'LG Councillor',
        article: 'an',
        title: 'LG Councillor (Ward level)',
        race: 'lga_ward',
        articleRoute: '/article/lga-councilor',
        resourceLink: null,
        runningMate: null,
        color: 'teal'
    },
    {
        name: 'House of Reps',
        role: 'House of Reps',
        race: 'house',
        articleRoute: null,
        resourceLink: 'https://nass.gov.ng/about/item/17',
        runningMate: null,
        color: 'emerald'
    },
    {
        name: 'Senate',
        role: 'Senate',
        race: 'senate',
        articleRoute: null,
        resourceLink: 'https://nass.gov.ng/about/item/9#',
        runningMate: null,
        color: 'blue'
    },
    {
        name: 'Governor',
        role: 'Governor',
        race: 'governor',
        articleRoute: null,
        resourceLink: 'https://nass.gov.ng/about/item/9#',
        runningMate: null,
        color: 'indigo'
    },
    {
        name: 'President',
        role: 'President',
        race: 'president',
        resourceLink: 'https://en.wikipedia.org/wiki/President_of_Nigeria',
        color: 'purple'
    }
];

const CandidatesTab = ({ electionData, state, isUserVerified, onGetNotifiedClick }) => {

    const { election = null, candidates = [], lga_budget = null } = electionData;
    const [activeRace, setActiveRace] = useState(election?.races?.length ? election.races[0] : null);
    const [activeParty, setActiveParty] = useState('');
    const [showParties, setShowParties] = useState(false);
    const [showTooltip, setShowTooltip] = useState(false);

    const candidatesList = candidates.map(candidate => {
        const position = groups.find(group => candidate.race === group.race)?.name;
        return {
            ...candidate,
            position,
        };
    })

    const activeGroups = groups.filter(group => {
            return !!election?.races?.find(race => race === group.race);
        }).map((group, index) => {
            const groupCandidates = candidatesList.filter(candidates => candidates.race === group.race);
            const filteredCandidates = groupCandidates.filter(candidates => activeParty === '' || candidates.partyCode === activeParty);
            const isActive = activeRace === null ? index === 0 : group.race === activeRace;
            return {
                ...group,
                isActive,
                total: candidates.length,
                candidates: filteredCandidates,
                constituency: candidates[0]?.constituency,
            };
        });

    const onSelectGroup = ({ isActive, race, role }) => {
        if (!isActive) {
            logEvent('Candidates', 'Opened Constituency Accordion', role)
        }

        setActiveRace(isActive ? '' : race)
    }

    const dates = election?.dates?.map(date => formatDateToString(date)) || [];


    return (
        <div className="grow flex flex-col gap-4 px-6 pb-8 pt-6">

            {(state === 'loading') && <div className="flex justify-center pt-2">
                <Loading size="lg" />
            </div>}

            {(state === 'error') && <Alert type="info">
                We are temporarily unable to show you candidates at this time, pls try again.
            </Alert>}


            {(state === 'success' && !!election) && <div className="flex flex-col">
                <div className="flex items-end">
                    <div className="w-full">
                        <h2 className="font-semibold text-gray-800">
                            LGA Elections
                            <span className="text-gray-300 px-1">-</span>{dates.join(' & ')}
                            <span className="text-gray-300 px-1">-</span>8am - 12pm
                        </h2>
                        {lga_budget && <h3 onMouseLeave={() => setShowTooltip(false)} className="relative inline-flex items-center mt-1 pr-4 text-lg">
                            <span className="font-bold ">{lga_budget.lganame}: {lga_budget.allocation}</span>
                            <span onMouseEnter={() => setShowTooltip(true)} className="inline-block text-sm pl-1 cursor-pointer">
                                <BiInfoCircle />
                            </span>
                            <div className={`${showTooltip ? 'visible' : 'invisible'} relative`} >

                                <div className="absolute bottom-3 -left-4 z-10 px-2 text-nowrap py-1.5 text-sm bg-black text-white font-semibold leading-tight rounded-sm shadow">
                                    This is the budget allocated for this local government. <br/>
                                    Data provided by <a href="https://me.budgit.org/" target="_blank" rel="noreferrer" className="underline">BudgIT</a>
                                </div>
                            </div>
                        </h3>}

                        {/* @TODO: Improvements for later  */}
                        <div className="mt-5 text-lg hidden">
                            <hr className="my-2 border-gray-100" />
                            <h2 className="">LGA: {lga_budget.lganame} </h2>
                            <h3 className="">
                                Bugdet:
                                <span className="font-bold"> {lga_budget.allocation}</span>
                            </h3>
                        </div>

                    </div>


                    {candidatesList.length > 0 && <div className="ml-auto">
                        <button onClick={() => setShowParties(!showParties)} className="flex items-center text-nowrap text-gray-600 gap-1 text-sm py-1.5 px-2.5 border border-gray-200 rounded-xl">
                            <span className={`${activeParty ? 'w-2 h-2' : 'w-0 h-0'} inline-block bg-emerald-400 rounded-full transition-all`}></span>
                            Parties
                            <BiFilter />
                        </button>
                    </div>}
                </div>

                {showParties && candidatesList.length > 0 && <RaceFilters activeParty={activeParty} onSelectParty={setActiveParty} />}
            </div>}

            {(state === 'success' && !!election && candidatesList.length === 0) && <>
                {isUserVerified
                    ? <Alert type="success">
                        <div>
                            <h3 className="font-bold">
                                You’ve subscribed!
                            </h3>
                            <p>
                                When LASIEC releases the full list of candidates, we will send an sms or WhatsApp message to notify you.
                            </p>
                        </div>
                    </Alert>
                    : <Alert type="neutral">
                        <p>
                            Unfortunately, LAISEC has not released a digital copy of the candidatates list for this election.
                            Please subscribe to get notified when/if it is released.
                        </p>
                        <Button className="underline text-lg" onClick={onGetNotifiedClick}>Subscribe</Button>
                    </Alert>
                }
            </>}

            {(state === 'success' && !election) && <div className="">
                <h2 className="text-lg font-bold ">There's no upcoming election in this area</h2>
            </div>}

            {(state === 'success' && candidatesList.length > 0) && <div className="flex flex-col gap-2">
                {activeGroups.map((group) => <CandidatesGroup
                    key={group.race}
                    group={group}
                    onSelectGroup={() => onSelectGroup(group)}
                />)}
            </div>}
        </div>
    )
}

export default CandidatesTab;
